import { createFileRoute, Link } from "@tanstack/react-router";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { CreatableSelect } from "@/components/Selects/select";
import type { MultiValue, SingleValue } from "react-select";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useTRPC } from "@/trpc";

type BusinessUnitUser = {
  id: string;
  user_id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  created_at: string;
  role: {
    id: string;
    name: string;
  } | null;
};

type UserSelectOption = {
  value: string;
  label: string;
  id: string;
  email: string;
  __isNew__?: boolean;
};

const BusinessUnitUsersPage = () => {
  const { businessUnitId, orgId } = Route.useParams();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<
    { id: string; email: string; isNew?: boolean }[]
  >([]);
  const [userToRemove, setUserToRemove] = useState<BusinessUnitUser | null>(
    null,
  );
  const [selectedRoleId, setSelectedRoleId] = useState<string | undefined>(
    undefined,
  );

  const { data: users = [], isLoading: usersLoading } = useQuery(
    trpc.organization.getBusinessUnitUsers.queryOptions({ businessUnitId }),
  );

  const { data: businessUnit, isLoading: businessUnitLoading } = useQuery(
    trpc.organization.getBusinessUnit.queryOptions({ businessUnitId }),
  );

  const { data: availableUsers = [] } = useQuery(
    trpc.adminDashboard.getUsers.queryOptions({ search: "" }),
  );

  const { data: availableRoles = [] } = useQuery(
    trpc.organization.getBuRoles.queryOptions({
      organizationBusinessUnitId: businessUnitId,
    }),
  );

  const addUserMutation = useMutation(
    trpc.organization.createBusinessUnitUser.mutationOptions({
      onSuccess: async (data) => {
        await queryClient.invalidateQueries(
          trpc.organization.getBusinessUnitUsers.queryOptions({
            businessUnitId,
          }),
        );
        setIsDialogOpen(false);
        setSelectedUsers([]);
        setSelectedRoleId(undefined);

        // Show success message
        if (data.total_added > 0) {
          console.log(
            `Successfully added ${data.total_added} user(s) to the business unit`,
          );
        }
      },
    }),
  );

  const removeUserMutation = useMutation(
    trpc.organization.deleteBusinessUnitUser.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          trpc.organization.getBusinessUnitUsers.queryOptions({
            businessUnitId,
          }),
        );
        setIsRemoveDialogOpen(false);
        setUserToRemove(null);
      },
    }),
  );

  const updateRoleMutation = useMutation(
    trpc.organization.updateBuUserRole.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          trpc.organization.getBusinessUnitUsers.queryOptions({
            businessUnitId,
          }),
        );
      },
    }),
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedUsers.length === 0 || !selectedRoleId) {
      return;
    }

    // Separate existing users and new emails
    const existingUserIds = selectedUsers
      .filter((user) => !user.isNew)
      .map((user) => user.id);
    const newEmails = selectedUsers
      .filter((user) => user.isNew)
      .map((user) => user.email);

    addUserMutation.mutate({
      businessUnitId,
      userIds: existingUserIds.length > 0 ? existingUserIds : undefined,
      emails: newEmails.length > 0 ? newEmails : undefined,
      roleId: selectedRoleId,
    });
  };

  const handleRemoveClick = (user: BusinessUnitUser) => {
    setUserToRemove(user);
    setIsRemoveDialogOpen(true);
  };

  // Transform available users for CreatableSelect
  const userOptions = availableUsers.map((user) => ({
    value: user.id,
    label: `${user.first_name || ""} ${user.last_name || ""} (${user.email ?? "No email"})`,
    id: user.id,
    email: user.email ?? "",
    __isNew__: false,
  }));

  const handleSelectChange = (
    options: MultiValue<UserSelectOption> | SingleValue<UserSelectOption>,
  ) => {
    if (!options) {
      setSelectedUsers([]);
      return;
    }

    const selectedOptions = Array.isArray(options) ? options : [options];
    const users = selectedOptions.map((option) => {
      if (option?.__isNew__) {
        // New email address created
        return {
          id: "",
          email: option.value,
          isNew: true,
        };
      } else {
        // Existing user selected
        return {
          id: option.id,
          email: option.email,
          isNew: false,
        };
      }
    });

    setSelectedUsers(users);
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidNewOption = (inputValue: string) => {
    return validateEmail(inputValue.trim());
  };

  const getNewOptionData = (inputValue: string) => ({
    value: inputValue.trim(),
    label: inputValue.trim(),
    id: "",
    email: inputValue.trim(),
    __isNew__: true,
  });

  const handleConfirmRemove = () => {
    removeUserMutation.mutate({
      businessUnitUserId: userToRemove?.id || "",
    });
  };

  const handleRoleChange = (userId: string, roleId: string) => {
    // Skip if "no-role" is selected (backend doesn't support removing roles yet)
    if (roleId === "no-role") {
      return;
    }
    updateRoleMutation.mutate({
      organizationBusinessUnitUserId: userId,
      roleId: roleId,
    });
  };

  return (
    <div className="container mx-auto py-8 px-2">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <Link
            to="/admin/organizations/$orgId/business-units"
            params={{ orgId }}
            className={buttonVariants({ variant: "outline" }) + " mb-4"}
          >
            ← Back to Organization
          </Link>
          <div className="flex justify-between items-center">
            <div>
              {businessUnitLoading ? (
                <Skeleton className="h-8 w-48 mb-2" />
              ) : (
                <h1 className="text-2xl font-semibold">{businessUnit?.name}</h1>
              )}
              <h2 className="text-lg text-gray-600 mt-1">
                Business Unit Users
              </h2>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button>Add Users</Button>
              </DialogTrigger>
              <DialogContent className="sm:min-w-[500px] p-4 sm:p-6">
                <DialogHeader>
                  <DialogTitle>Add Users to Business Unit</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium mb-1.5 block">
                      Users
                    </Label>
                    <CreatableSelect
                      isMulti
                      value={selectedUsers.map((user) => ({
                        value: user.isNew ? user.email : user.id,
                        label: user.isNew
                          ? user.email
                          : userOptions.find((u) => u.id === user.id)?.label ||
                            user.email,
                        id: user.id,
                        email: user.email,
                        __isNew__: user.isNew,
                      }))}
                      onChange={handleSelectChange}
                      options={userOptions}
                      placeholder="Select users or enter email addresses..."
                      isClearable
                      isValidNewOption={isValidNewOption}
                      getNewOptionData={getNewOptionData}
                      formatCreateLabel={(inputValue) =>
                        `Invite "${inputValue}"`
                      }
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-1.5 block">
                      Role
                    </Label>
                    <Select
                      value={selectedRoleId}
                      onValueChange={setSelectedRoleId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableRoles.map((role) => (
                          <SelectItem key={role.id} value={role.id}>
                            {role.name}
                            {role.isDefault && " (Default)"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsDialogOpen(false)}
                      className="w-full sm:w-auto"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        addUserMutation.isPending ||
                        selectedUsers.length === 0 ||
                        !selectedRoleId
                      }
                      className="w-full sm:w-auto"
                    >
                      {addUserMutation.isPending
                        ? "Adding..."
                        : selectedUsers.length === 1
                          ? "Add User"
                          : "Add Users"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {usersLoading ? (
          <Card className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...Array(3)].map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-48" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-8 w-16" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        ) : !users || users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-gray-500 bg-gray-50 rounded-lg">
            <svg
              className="w-12 h-12 mb-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
            <h3 className="text-lg font-semibold mb-2">No Users Yet</h3>
            <p className="text-sm text-center mb-4">
              Get started by adding users to this business unit
            </p>
            <Button
              onClick={() => setIsDialogOpen(true)}
              variant="outline"
              size="sm"
            >
              Add Users
            </Button>
          </div>
        ) : (
          <Card className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user: BusinessUnitUser) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      {user.first_name || ""} {user.last_name || ""}
                    </TableCell>
                    <TableCell>{user.email ?? "—"}</TableCell>
                    <TableCell>
                      <Select
                        value={user.role?.id || "no-role"}
                        onValueChange={(roleId) =>
                          handleRoleChange(user.id, roleId)
                        }
                        disabled={updateRoleMutation.isPending}
                      >
                        <SelectTrigger className="w-[200px]">
                          <SelectValue placeholder="Select a role" />
                        </SelectTrigger>
                        <SelectContent>
                          {!user.role && (
                            <SelectItem value="no-role" disabled>
                              No role assigned
                            </SelectItem>
                          )}
                          {availableRoles.map((role) => (
                            <SelectItem key={role.id} value={role.id}>
                              {role.name}
                              {role.isDefault && " (Default)"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleRemoveClick(user)}
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}

        <Dialog open={isRemoveDialogOpen} onOpenChange={setIsRemoveDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Remove User</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p>
                Are you sure you want to remove {userToRemove?.first_name || ""}{" "}
                {userToRemove?.last_name || ""} from this business unit?
              </p>
              <div className="flex justify-end gap-4">
                <Button
                  variant="outline"
                  onClick={() => setIsRemoveDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleConfirmRemove}
                  disabled={removeUserMutation.isPending}
                >
                  {removeUserMutation.isPending ? "Removing..." : "Remove"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export const Route = createFileRoute(
  "/_admin/admin/organizations/$orgId/business-units/$businessUnitId/users/",
)({
  component: BusinessUnitUsersPage,
});
